import {boardCellKey} from '@github-ui/board-core'

import {
  type BoardInitialPayload,
  type BoardPayloadCell,
  type BoardPayloadItem,
  toBoardData as toBoardDataShape,
} from './issues/board-payload'
import {normalizeBoardMoves} from './board-move-axes'
import type {BoardCellCursors, SavedViewsBoardData} from './types'

/**
 * Keyed by board-core's own cell key: bucket keys are opaque and may hold any character, so a
 * bare join collides and a collision would hand one cell another's cursor.
 */
export function cellCursors(
  cells: ReadonlyArray<Pick<BoardPayloadCell, 'columnKey' | 'rowKey' | 'itemsCursor'>>,
): BoardCellCursors {
  const cursors = new Map<string, string>()
  for (const cell of cells) {
    if (cell.itemsCursor != null) cursors.set(boardCellKey(cell), cell.itemsCursor)
  }
  return cursors
}

/**
 * Maps a payload into everything the host needs: the board `data`, the axis field identifiers
 * that drive grouping, and where each cell left off. Pure — no React, no fetch, no domain
 * rules.
 *
 * The `data` mapping is delegated to the payload module's `toBoardData`, so the D16
 * discipline — four stated axis booleans, per-cell `totalCount`/`hasMoreItems` carried
 * rather than inferred from `items.length` — and the no-value bucket name live in exactly
 * one place.
 */
export function toBoardData(payload: BoardInitialPayload): SavedViewsBoardData<BoardPayloadItem> {
  return normalizeBoardMoves({
    data: toBoardDataShape(payload),
    columnField: payload.columnField,
    rowField: payload.rowField,
    viewerCanMoveItems: payload.viewerCanMoveItems,
    viewerCanMoveBetweenColumns: payload.viewerCanMoveBetweenColumns,
    viewerCanMoveBetweenRows: payload.viewerCanMoveBetweenRows,
    cellCursors: cellCursors(payload.cells),
    columnsCursor: payload.columnsCursor,
    rowsCursor: payload.rowsCursor,
  })
}
